import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme, legacyTheme } from './muiTheme';
import { ThemeName, ThemeTokens, tokensFor } from './tokens';

export type ThemeMode = 'dark' | 'light' | 'system';

export const THEME_MODE_KEY = 'ds-theme-mode';

/**
 * The AI-app pages that predate the brand palette. They paint their own light,
 * purple-accented UI and keep the MUI theme they were built against, in either mode.
 */
const LEGACY_ROUTES = [
    '/resume-ranking',
    '/news-analysis',
    '/credit-checker',
    '/video-converter',
    '/docs-insights',
    '/survey-insights',
    '/data-analytics',
    '/customer-service',
    '/loan-processing',
    '/asset-records-intelligence',
    '/invitation/accept',
];

export const isLegacyRoute = (pathname: string): boolean =>
    LEGACY_ROUTES.some(route => pathname === route || pathname.startsWith(`${route}/`));

// Storage can be unavailable (private windows, blocked site data); the theme must still render.
const readStorage = (key: string): string | null => {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

const writeStorage = (key: string, value: string) => {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // Not remembered across reloads; the choice still applies for this session.
    }
};

const readStoredMode = (): ThemeMode => {
    const stored = readStorage(THEME_MODE_KEY);
    return stored === 'dark' || stored === 'light' || stored === 'system' ? stored : 'system';
};

const DARK_QUERY = '(prefers-color-scheme: dark)';

const useSystemPrefersDark = (): boolean => {
    const query = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(DARK_QUERY) : null;
    const [prefersDark, setPrefersDark] = useState(() => query?.matches ?? true);
    useEffect(() => {
        if (!query) return;
        const onChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
        // The query object is recreated each render; the media query itself never changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return prefersDark;
};

interface ThemeModeContextValue {
    /** What the user picked. */
    mode: ThemeMode;
    /** What is painted on brand pages: `system` resolved against the OS setting. */
    resolved: ThemeName;
    setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
    mode: 'dark',
    resolved: 'dark',
    setMode: () => undefined,
});

export const useThemeMode = () => useContext(ThemeModeContext);

/**
 * The active theme's tokens as hex, for what CSS variables cannot reach: canvas
 * and SVG libraries (cytoscape, recharts, sankey). DOM styling should use dsVar.
 */
export const useThemeTokens = (): ThemeTokens => tokensFor(useThemeMode().resolved);

/**
 * Owns the dark/light choice: remembers it, follows the OS for `system`, marks
 * <html data-theme> so the CSS tokens (and portaled MUI menus, which sit outside
 * #root) switch together, and supplies the matching MUI theme.
 *
 * Must sit inside the router: legacy pages are recognised by their path.
 */
export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
    const prefersDark = useSystemPrefersDark();
    const { pathname } = useLocation();
    const legacy = isLegacyRoute(pathname);

    const resolved: ThemeName = mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;
    const painted: ThemeName = legacy ? 'light' : resolved;

    const setMode = useCallback((next: ThemeMode) => {
        setModeState(next);
        writeStorage(THEME_MODE_KEY, next);
    }, []);

    // Before paint, so a page never shows a frame in the other theme's tokens.
    useLayoutEffect(() => {
        const root = document.documentElement;
        root.dataset.theme = painted;
        // Lets styles.css keep the legacy pages' Inter headings.
        if (legacy) root.dataset.ui = 'legacy';
        else delete root.dataset.ui;
    }, [painted, legacy]);

    const muiTheme = useMemo(() => (legacy ? legacyTheme : createAppTheme(resolved)), [legacy, resolved]);
    const value = useMemo(() => ({ mode, resolved, setMode }), [mode, resolved, setMode]);

    return (
        <ThemeModeContext.Provider value={value}>
            <ThemeProvider theme={muiTheme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeModeContext.Provider>
    );
};
